import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AryLoadingButton from 'framework/base/components/AryLoadingButton';
import AryModal from 'framework/base/components/AryModal';
import {
  ARCHIVE_BANK,
  GET_BANK_BOOK_STATUS,
  GET_STATE,
  SUBMIT_BALANCE_BASED_BANK_CHECKOUT
} from 'framework/base/constants/aryGraphqlSchemas';
import arySystemSettings from 'framework/base/constants/arySystemSettings';
import useAryDelayedMutation from 'framework/base/hooks/useAryDelayedMutation';
import useSystemSettingsContext from 'framework/base/hooks/useSystemSettingsContext';
import { getTimeZoneFormattedDate } from 'framework/base/utils/Utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Stack } from '@mui/system';
import { setShouldRefetchBanks } from 'redux/slices/systemSlice';
import AryBaseTable from 'framework/desktop/BaseTable/AryBaseTable';
import { useAryLazyQuery, useAryMutation } from '../hooks/useAryGraphql';
import { formatMoneyAmount } from './AryNumberFormatter';
import useTableDataParser from '../hooks/useTableDataParser';

const ArchiveBankCheckout = ({ open, handleClose }) => {
  const { t } = useTranslation();
  const { statusParser, textParser } = useTableDataParser();
  const theme = useTheme();
  const { systemSettings } = useSystemSettingsContext();
  const bankAccountId = useSelector(
    (state) => state.systemReducer.bankAccountId
  );
  const [bankStatus, setBankStatus] = useState({});
  const dispatch = useDispatch();

  const columns = {
    currency: {
      label: t('currency'),
      key: 'currency',
      renderCell: (row, key) => textParser(row, key)
    },
    balance: {
      label: t('BalanceAmount'),
      key: 'balance',
      renderCell: (row, key) => formatMoneyAmount(row[key])
    },
    isDebtor: {
      label: t('Status'),
      key: 'isDebtor',
      renderCell: (row, key) => statusParser(row, key)
    }
  };

  const [
    getBankBookStatus,
    { data: getBankBookStatusQueryData, loading: getBankStatusQueryLoading }
  ] = useAryLazyQuery({
    gqlConfig: GET_BANK_BOOK_STATUS,
    items: `
      accountNumber
      currency
      currencyCode
      deposit
      withdraw
      balance
      isDebtor
      checkoutDate
      checkoutBalance
      checkoutIsDebtor
      checkoutExists
    `
  });

  useEffect(() => {
    getBankBookStatus({
      bankId: bankAccountId
    });
  }, [bankAccountId]);

  useEffect(() => {
    if (getBankBookStatusQueryData) {
      const bankObj = {
        ...getBankBookStatusQueryData,
        isDebtor: !getBankBookStatusQueryData?.isDebtor
      };

      setBankStatus(bankObj);
    }
  }, [getBankBookStatusQueryData]);

  const [archiveBank, { loading: archiveBankLoading }] =
    useAryMutation(ARCHIVE_BANK);

  const [
    submitBalanceBasedBankCheckout,
    { loading: submitBalanceBasedBankCheckoutLoading }
  ] = useAryDelayedMutation({
    gqlConfig: SUBMIT_BALANCE_BASED_BANK_CHECKOUT,
    type: GET_STATE.type.ACCOUNTING,
    onSucceed: async () => {
      try {
        const response = await archiveBank(bankAccountId);
        if (response.data) {
          dispatch(setShouldRefetchBanks(true));
        }
      } catch (error) {
        console.error('Archive branch error:', error);
      } finally {
        handleClose();
      }
    }
  });

  const handleCheckout = () => {
    submitBalanceBasedBankCheckout({
      bankAccountId,
      toDate: String(new Date().getTime()),
      amount: Math.abs(bankStatus.balance),
      currencyCode: bankStatus.currencyCode,
      isDebtor: bankStatus.isDebtor
    });
  };

  return (
    <AryModal
      open={open}
      handleClose={() => {
        handleClose();
      }}
      title={t('CheckoutAccount')}
      style={{ overflow: 'visible' }}
    >
      <Grid item xs={12}>
        <Grid
          container
          rowSpacing={theme.padding.gridRowSpacing}
          columnSpacing={theme.padding.gridColumnSpacing}
        >
          <Grid container xs={12} gap={theme.padding.gridRowSpacing}>
            <Grid item xs={12} overflow="auto" maxHeight="50vh">
              <AryBaseTable
                columns={columns}
                filteredColumnsList={['currency', 'balance', 'isDebtor']}
                data={[bankStatus]}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              width="100%"
              justifyContent="flex-end"
              gap={theme.padding.gridColumnSpacing}
            >
              <Typography variant="c2Bold">
                {`${t('To Date')}: `}
                {getTimeZoneFormattedDate(
                  new Date().getTime(),
                  systemSettings[arySystemSettings.isGregorian],
                  systemSettings[arySystemSettings.timeZone],
                  systemSettings[arySystemSettings.isDigitsLatin]
                )}
              </Typography>
              <Stack direction="row" gap={theme.padding.gridColumnSpacing}>
                <AryLoadingButton
                  disabled={getBankStatusQueryLoading}
                  loading={
                    submitBalanceBasedBankCheckoutLoading || archiveBankLoading
                  }
                  size="medium"
                  variant="contained"
                  fullWidth
                  sx={{ height: theme.componentSizes.inputBoxHeight }}
                  onClick={handleCheckout}
                >
                  {t('SubmitNewCheckout')}
                </AryLoadingButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </AryModal>
  );
};

export default ArchiveBankCheckout;
